import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import styles from "./Oppgaver.module.css";
import { apen, faseFarge, fmtDato, iDag, pluss, type Oppgave } from "../../lib/oppgaver";

/** Prosjektfasene (portal.Phases, 002_seed_data.sql), i rekkefølge. */
export const FASER = [
  { id: 1, kode: "AKKV", navn: "Akkvisisjon" },
  { id: 2, kode: "REG", navn: "Regulering" },
  { id: 3, kode: "SALG", navn: "Salg, marked og kunde" },
  { id: 4, kode: "FORP", navn: "Forprosjekt" },
  { id: 5, kode: "GJEN", navn: "Gjennomføring" },
  { id: 6, kode: "ETTER", navn: "Ettermarked" },
];

/** Frister de neste dagene markeres i oransje (forfalte i rødt). */
const NAER_DAGER = 3;

export const faseStil = (kode: string | null): CSSProperties => {
  const f = faseFarge(kode);
  return { ["--fc" as string]: f.main, ["--fs" as string]: f.soft };
};

export function FaseMerke({ kode, navn }: { kode: string | null; navn: string | null }) {
  if (!navn) return <span className={styles.dempet}>—</span>;
  return <span className={styles.fase} style={faseStil(kode)}>{navn}</span>;
}

export function FristTekst({ o }: { o: Pick<Oppgave, "frist" | "status"> }) {
  if (!o.frist) return <span className={`${styles.frist} ${styles.dempet}`}>Ingen frist</span>;
  if (!apen(o)) return <span className={styles.frist}>{fmtDato(o.frist)}</span>;
  const idag = iDag();
  if (o.frist < idag) return <span className={styles.fristForfalt}>Forfalt {fmtDato(o.frist)}</span>;
  if (o.frist === idag) return <span className={styles.fristSnart}>I dag</span>;
  if (o.frist <= pluss(idag, NAER_DAGER)) return <span className={styles.fristSnart}>{fmtDato(o.frist)}</span>;
  return <span className={styles.frist}>{fmtDato(o.frist)}</span>;
}

/** Hvor oppgaven kommer fra, når den ikke er fra malen. */
export function KildeMerke({ o }: { o: Oppgave }) {
  if (o.kilde === "Dokument")
    return <span className={o.arsak?.startsWith("Vedtak") ? styles.chipMyn : styles.chipDok}>{o.arsak ?? "Fra dokumentlesingen"}</span>;
  if (o.kilde === "Egen") return <span className={styles.chipAkt}>Lagt til på prosjektet</span>;
  return null;
}

/** Ansvarlig som tekst, med «ekstern» for rådgivere. */
export function AnsvarligTekst({ o }: { o: Pick<Oppgave, "ansvarlig" | "radgiver"> }) {
  if (!o.ansvarlig) return <span className={styles.dempet}>Ingen ansvarlig</span>;
  return (
    <>
      {o.ansvarlig}
      {o.radgiver && <span className={styles.chipEkstern} title={[o.radgiver.rolle, o.radgiver.firma].filter(Boolean).join(", ")}>ekstern</span>}
    </>
  );
}

/** Kort melding nederst på skjermen («Gitt til Kari …»). */
export function useMelding() {
  const [tekst, setTekst] = useState<string | null>(null);
  const tid = useRef<number>();
  const melding = useCallback((t: string) => {
    setTekst(t);
    window.clearTimeout(tid.current);
    tid.current = window.setTimeout(() => setTekst(null), 3500);
  }, []);
  useEffect(() => () => window.clearTimeout(tid.current), []);
  const vis = tekst ? <div className={styles.toast} role="status">{tekst}</div> : null;
  return [melding, vis] as const;
}
